import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
    Plug, Plus, Search, RefreshCw, Globe, Shield, AlertTriangle,
    CheckCircle2, Hourglass, ArrowUpRight, X, TrendingUp, Database, Cloud,
} from "lucide-react";
import ClientPicker from "@/components/ClientPicker";
import MongoServersPanel from "@/components/mongo/servers/MongoServersPanel";
import { useAuth } from "@/context/AuthContext";


// ============================================================
//  System (non-tenant) connections — currently just the
//  document-store S3 bucket used by ingest endpoints' `document_id`
//  path. Renders at the top of the page; tenant connections sit
//  below. Credentials are never returned by the API.
// ============================================================
function SystemConnectionsPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/connections/system/document-store");
            setData(data);
            setTestResult(null);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load system connections");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const runTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await api.post("/admin/connections/system/document-store/test");
            setTestResult(data);
            if (data.ok) toast.success("Document-store connection OK");
            else toast.error(data.detail || "Connection test failed");
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Test request failed");
        } finally {
            setTesting(false);
        }
    };

    const cfg = data?.config || {};
    const configured = !!cfg.configured;

    return (
        <div className="rounded-lg border border-white/10 bg-gradient-to-br from-white/[0.025] to-transparent p-4"
             data-testid="system-connections-panel">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-violet-300" strokeWidth={1.5} />
                    <h2 className="text-sm font-medium">System connections</h2>
                    <span className="text-[10px] text-white/40 font-mono">— shared across all tenants, configured via env vars per deploy instance</span>
                </div>
                <button onClick={load} disabled={loading}
                    data-testid="system-connections-refresh"
                    className="px-2 py-1 text-[11px] border border-white/10 rounded-md hover:bg-white/5 inline-flex items-center gap-1.5">
                    <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
                </button>
            </div>

            <div className="rounded-md border border-white/10 bg-white/[0.02] p-4"
                 data-testid="system-connection-card-document-store">
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-start gap-3">
                        <div className="w-9 h-9 rounded-md bg-violet-400/10 border border-violet-400/20 flex items-center justify-center flex-shrink-0">
                            <Cloud className="w-4 h-4 text-violet-300" strokeWidth={1.5} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <div className="text-sm font-medium">{data?.label || "Document Store (S3)"}</div>
                                {configured ? (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono border bg-emerald-300/10 border-emerald-300/30 text-emerald-200"
                                          data-testid="system-conn-status-configured">
                                        <CheckCircle2 className="w-3 h-3" /> configured
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono border bg-amber-300/10 border-amber-300/30 text-amber-200"
                                          data-testid="system-conn-status-not-configured">
                                        <AlertTriangle className="w-3 h-3" /> not configured
                                    </span>
                                )}
                            </div>
                            <div className="text-[11px] text-white/50 mt-1 max-w-2xl leading-relaxed">
                                {data?.description}
                            </div>
                        </div>
                    </div>
                    <button onClick={runTest} disabled={testing || !configured}
                        data-testid="system-conn-test-btn"
                        className="px-3 py-1.5 text-xs border border-violet-300/30 text-violet-200 rounded-md hover:bg-violet-300/10 inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
                        {testing ? <Hourglass className="w-3 h-3 animate-spin" /> : <Plug className="w-3 h-3" />}
                        {testing ? "Testing…" : "Test connection"}
                    </button>
                </div>

                {/* Config snapshot — bucket, region, masked AK, secret-present */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-[11px]">
                    <KV k="Bucket"        v={cfg.bucket || "—"} testid="system-conn-bucket" />
                    <KV k="Region"        v={cfg.region || "—"} testid="system-conn-region" />
                    <KV k="Access key ID" v={cfg.access_key_id_masked || "—"} testid="system-conn-access-key" />
                    <KV k="Secret"        v={cfg.secret_present ? "set in env" : "missing"}
                        tone={cfg.secret_present ? "ok" : "warn"}
                        testid="system-conn-secret-status" />
                </div>

                {testResult && (
                    <div className={`mt-3 px-3 py-2 rounded-md text-[11px] flex items-start gap-2 ${
                        testResult.ok
                            ? "border border-emerald-300/30 bg-emerald-300/5 text-emerald-100"
                            : "border border-rose-300/30 bg-rose-300/5 text-rose-100"
                    }`} data-testid="system-conn-test-result">
                        {testResult.ok
                            ? <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                            : <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
                        <span className="font-mono whitespace-pre-wrap break-words">{testResult.detail}</span>
                    </div>
                )}

                {/* Env-var help — only shown when not configured, so deployers know what to set */}
                {!configured && data?.env_keys && (
                    <div className="mt-3 px-3 py-2 rounded-md border border-amber-300/20 bg-amber-300/5 text-[10px] text-amber-100/90">
                        <div className="font-medium mb-1 text-amber-200 text-[11px]">
                            <Database className="inline w-3 h-3 mr-1" /> To configure, set these env vars in <code>/app/backend/.env</code> and restart:
                        </div>
                        <ul className="font-mono space-y-0.5">
                            {data.env_keys.map((k) => <li key={k}>· {k}</li>)}
                        </ul>
                    </div>
                )}

                <div className="text-[10px] text-white/35 mt-3 leading-relaxed">
                    When configured, the protocol / CTA / Budget / EDC ingest endpoints accept an optional
                    <code className="text-white/65 mx-1">document_id</code> form field — the S3 object key.
                    The body is fetched from this bucket instead of multipart upload. Each deploy instance
                    can point at its own bucket via the env vars above.
                </div>
            </div>
        </div>
    );
}

function KV({ k, v, tone, testid }) {
    const palette = tone === "ok" ? "text-emerald-200"
        : tone === "warn" ? "text-amber-200"
        : "text-white/85";
    return (
        <div data-testid={testid}>
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-0.5">{k}</div>
            <div className={`font-mono text-xs ${palette}`}>{v}</div>
        </div>
    );
}

const STATUS_BADGE = {
    draft:             { className: "bg-white/5 border-white/15 text-white/70",         label: "draft" },
    testing:           { className: "bg-amber-300/10 border-amber-300/30 text-amber-200", label: "testing" },
    published:         { className: "bg-emerald-300/10 border-emerald-300/30 text-emerald-200", label: "published" },
    retired:           { className: "bg-white/5 border-white/10 text-white/40",          label: "retired" },
    failed_validation: { className: "bg-rose-300/10 border-rose-300/30 text-rose-200",   label: "failed validation" },
};

function StatusPill({ status }) {
    const conf = STATUS_BADGE[status] || STATUS_BADGE.draft;
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono border ${conf.className}`}
            data-testid={`connection-status-${status}`}>
            {conf.label}
        </span>
    );
}

function CreateModal({ onClose, onCreated, defaultClientId }) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [environment, setEnvironment] = useState("dev");
    const [baseUrl, setBaseUrl] = useState("");
    const [authType, setAuthType] = useState("none");
    const [clientId, setClientId] = useState(defaultClientId || "");
    const [tags, setTags] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [scBackends, setScBackends] = useState([]);
    const isSiteCentric = authType === "sitecentric_client_key";

    useEffect(() => {
        if (!isSiteCentric || scBackends.length) return;
        api.get("/admin/connections/_meta/catalog")
            .then(({ data }) => setScBackends(data?.sitecentric?.backends || []))
            .catch(() => setScBackends([]));
    }, [isSiteCentric, scBackends.length]);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const body = {
                name: name.trim(),
                description: description.trim() || undefined,
                environment,
                base_url: baseUrl.trim(),
                auth_type: authType,
                tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
            };
            const params = new URLSearchParams();
            if (clientId) params.set("client_id", clientId);
            const { data } = await api.post(`/admin/connections?${params.toString()}`, body);
            toast.success(`Connection created — ${data.name}`);
            onCreated(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            data-testid="connection-create-modal">
            <form onSubmit={submit} className="w-full max-w-xl bg-[#0c0c0c] border border-white/10 rounded-xl p-5 space-y-3">
                <div className="flex items-center justify-between">
                    <h2 className="text-base">New Connection</h2>
                    <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                        <div className="text-xs text-white/50 mb-1">Name <span className="text-rose-400">*</span></div>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} required
                            data-testid="conn-create-name"
                            className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                    </label>
                    <label className="block">
                        <div className="text-xs text-white/50 mb-1">Environment <span className="text-rose-400">*</span></div>
                        <select value={environment} onChange={(e) => setEnvironment(e.target.value)}
                            data-testid="conn-create-environment"
                            className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm">
                            <option value="dev">Development</option>
                            <option value="stage">Stage</option>
                            <option value="prod">Production</option>
                        </select>
                    </label>
                </div>
                <ClientPicker
                    value={clientId} onChange={setClientId}
                    label="Tenant / Client" required testId="conn-create-client"
                    excludeDisabled
                />
                <label className="block">
                    <div className="text-xs text-white/50 mb-1">Base URL <span className="text-rose-400">*</span></div>
                    <input type="url" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} required
                        placeholder="https://api.partner.com/v1"
                        data-testid="conn-create-base-url"
                        className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
                </label>
                <label className="block">
                    <div className="text-xs text-white/50 mb-1">Authentication</div>
                    <select value={authType} onChange={(e) => setAuthType(e.target.value)}
                        data-testid="conn-create-auth-type"
                        className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm">
                        <option value="none">None</option>
                        <option value="api_key_header">API key (header)</option>
                        <option value="api_key_query">API key (query)</option>
                        <option value="basic">Basic</option>
                        <option value="bearer">Bearer token</option>
                        <option value="oauth2_client_credentials">OAuth2 client credentials</option>
                        <option value="custom_header">Custom header</option>
                        <option value="sitecentric_client_key">SiteCentric · client key</option>
                    </select>
                </label>
                {isSiteCentric && (
                    <div className="rounded-md border border-sky-300/20 bg-sky-300/[0.04] px-3 py-2 space-y-2" data-testid="conn-create-sitecentric">
                        <div className="text-[11px] text-sky-100/90">
                            No secret needed: the API key of the client each run works for is exchanged for a SiteCentric session token, kept for that task and logged out afterwards.
                        </div>
                        <label className="block">
                            <div className="text-xs text-white/50 mb-1">SiteCentric backend (fills the base URL)</div>
                            <select value="" onChange={(e) => e.target.value && setBaseUrl(e.target.value)}
                                data-testid="conn-create-sitecentric-backend"
                                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm">
                                <option value="">— pick a market × environment, or type the URL above —</option>
                                {scBackends.map((b) => (
                                    <option key={b.backend_url} value={b.backend_url}>{b.label} · {b.environment_label} · {b.backend_url}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                )}
                <label className="block">
                    <div className="text-xs text-white/50 mb-1">Description</div>
                    <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
                        data-testid="conn-create-description"
                        className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                </label>
                <label className="block">
                    <div className="text-xs text-white/50 mb-1">Tags (comma-separated)</div>
                    <input type="text" value={tags} onChange={(e) => setTags(e.target.value)}
                        placeholder="weather, vendor"
                        className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
                </label>
                <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={onClose}
                        className="px-3 py-1.5 text-xs border border-white/15 rounded-md hover:bg-white/5">
                        Cancel
                    </button>
                    <button type="submit" disabled={submitting || !name || !baseUrl || !clientId}
                        data-testid="conn-create-submit"
                        className="px-3 py-1.5 text-xs bg-white text-black rounded-md disabled:opacity-40 inline-flex items-center gap-1.5">
                        {submitting ? <Hourglass className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                        Create draft
                    </button>
                </div>
            </form>
        </div>
    );
}

export default function ConnectionsPage() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [filter, setFilter] = useState({
        status: searchParams.get("status") || "",
        environment: searchParams.get("environment") || "",
        client_id: searchParams.get("client_id") || "",
        search: searchParams.get("search") || "",
        common: searchParams.get("common") || "",
    });
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);

    useEffect(() => {
        const sp = new URLSearchParams();
        for (const [k, v] of Object.entries(filter)) {
            if (v && String(v).trim()) sp.set(k, String(v).trim());
        }
        setSearchParams(sp, { replace: true });
        // eslint-disable-next-line
    }, [filter]);

    const params = useMemo(() => {
        const sp = new URLSearchParams();
        for (const [k, v] of Object.entries(filter)) {
            if (v && String(v).trim()) sp.set(k, String(v).trim());
        }
        sp.set("limit", "100");
        return sp.toString();
    }, [filter]);

    const load = async () => {
        setLoading(true);
        setError(null);
        try {
            const { data } = await api.get(`/admin/connections?${params}`);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            setError(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="space-y-5" data-testid="connections-page">
            <div className="flex items-end justify-between gap-4">
                <div>
                    <h1 className="text-2xl flex items-center gap-2.5">
                        <Plug className="w-6 h-6 opacity-70" strokeWidth={1.5} /> Connections
                    </h1>
                    <p className="text-xs text-white/50 mt-1 max-w-2xl">
                        REST integrations available to Flow Builder. Define endpoints,
                        store credentials securely, test, then publish to use as an
                        <span className="font-mono text-white/75"> API Call </span>
                        tile inside published flows.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load}
                        data-testid="connections-refresh"
                        className="px-2.5 py-1.5 text-xs border border-white/10 rounded-md hover:bg-white/5 inline-flex items-center gap-1.5">
                        <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </button>
                    <Link to="/connections/_analytics"
                        data-testid="connections-analytics-link"
                        className="px-2.5 py-1.5 text-xs border border-violet-300/30 text-violet-200 rounded-md hover:bg-violet-300/10 inline-flex items-center gap-1.5">
                        <TrendingUp className="w-3 h-3" /> Analytics
                    </Link>
                    <button onClick={() => setCreateOpen(true)}
                        data-testid="connections-create-open"
                        className="px-3 py-1.5 text-xs bg-white text-black rounded-md inline-flex items-center gap-1.5">
                        <Plus className="w-3 h-3" /> New connection
                    </button>
                </div>
            </div>

            <SystemConnectionsPanel />
            {user?.role === "super_admin" && <MongoServersPanel />}

            {/* Filter bar */}
            <div className="flex flex-wrap items-end gap-2.5 px-3 py-2.5 rounded-lg border border-white/10 bg-white/[0.02]">
                <div className="flex items-center gap-1 flex-wrap">
                    {["", "draft", "testing", "published", "retired"].map((s) => (
                        <button key={s || "all"}
                            onClick={() => setFilter((f) => ({ ...f, status: s }))}
                            data-testid={`connections-filter-${s || "all"}`}
                            className={`px-2 py-1 rounded-full text-[10px] uppercase tracking-wider transition ${
                                filter.status === s
                                    ? "bg-white text-black"
                                    : "bg-white/5 hover:bg-white/10 text-white/70 border border-white/10"
                            }`}>
                            {s || "All"}
                        </button>
                    ))}
                </div>
                <select value={filter.environment}
                    onChange={(e) => setFilter((f) => ({ ...f, environment: e.target.value }))}
                    data-testid="connections-filter-env"
                    className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs">
                    <option value="">All environments</option>
                    <option value="dev">Development</option>
                    <option value="stage">Stage</option>
                    <option value="prod">Production</option>
                </select>
                <select value={filter.common}
                    onChange={(e) => setFilter((f) => ({ ...f, common: e.target.value }))}
                    data-testid="connections-filter-common"
                    className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-xs">
                    <option value="">All connections</option>
                    <option value="true">Common only</option>
                    <option value="false">Tenant only</option>
                </select>
                <ClientPicker
                    value={filter.client_id}
                    onChange={(v) => setFilter((f) => ({ ...f, client_id: v }))}
                    compact width="w-56"
                    anyOptionLabel="all tenants"
                    testId="connections-filter-client"
                />
                <div className="relative flex-1 min-w-[180px]">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-white/40" />
                    <input type="text" placeholder="Search name, URL, tags…"
                        value={filter.search}
                        onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
                        data-testid="connections-filter-search"
                        className="w-full bg-white/5 border border-white/10 rounded-md pl-7 pr-2 py-1 text-xs" />
                </div>
                <span className="text-[10px] uppercase tracking-wider font-mono text-white/40 ml-auto">
                    {total} total
                </span>
            </div>

            {error && (
                <div className="px-3 py-2 rounded-md border border-rose-300/30 bg-rose-300/10 text-xs text-rose-200 inline-flex items-center gap-2"
                    data-testid="connections-error">
                    <AlertTriangle className="w-3 h-3" /> {error}
                </div>
            )}

            {/* List */}
            <div className="border border-white/10 rounded-lg overflow-hidden bg-white/[0.02]">
                <table className="w-full text-sm">
                    <thead className="bg-white/[0.04] text-[10px] uppercase tracking-wider text-white/50">
                        <tr>
                            <th className="text-left px-3 py-2">Name</th>
                            <th className="text-left px-3 py-2">Env</th>
                            <th className="text-left px-3 py-2">Tenant</th>
                            <th className="text-left px-3 py-2">Status</th>
                            <th className="text-left px-3 py-2">Auth</th>
                            <th className="text-left px-3 py-2">Endpoints</th>
                            <th className="text-left px-3 py-2">Flows</th>
                            <th className="text-left px-3 py-2">Last test</th>
                            <th className="text-left px-3 py-2">Updated</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody data-testid="connections-tbody">
                        {!loading && items.length === 0 && (
                            <tr>
                                <td colSpan={10} className="px-3 py-10 text-center text-white/40 text-xs">
                                    No connections yet.
                                    {" "}<button onClick={() => setCreateOpen(true)} className="underline opacity-80">
                                        Create the first one.
                                    </button>
                                </td>
                            </tr>
                        )}
                        {items.map((c) => (
                            <tr key={c.id} data-testid={`connection-row-${c.id}`}
                                className="border-t border-white/5 hover:bg-white/[0.02]">
                                <td className="px-3 py-2.5">
                                    <div className="flex items-center gap-2">
                                        <Plug className="w-3 h-3 text-white/50" />
                                        <Link to={`/connections/${c.id}`} className="hover:underline">
                                            {c.name}
                                        </Link>
                                        {c.is_common && (
                                            <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-violet-300/10 border border-violet-300/30 text-violet-200"
                                                title="Platform-common — visible to every tenant">
                                                COMMON
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[10px] text-white/40 font-mono mt-0.5 max-w-[260px] truncate">
                                        {c.base_url}
                                    </div>
                                </td>
                                <td className="px-3 py-2.5 text-xs text-white/70">{c.environment}</td>
                                <td className="px-3 py-2.5 text-[11px] text-white/60 font-mono">{c.client_id}</td>
                                <td className="px-3 py-2.5"><StatusPill status={c.status} /></td>
                                <td className="px-3 py-2.5 text-[11px] font-mono text-white/65">
                                    {c.auth_type === "sitecentric_client_key"
                                        ? <span className="text-sky-200" data-testid={`conn-auth-sitecentric-${c.id}`}>SiteCentric · client key</span>
                                        : c.auth_type}
                                </td>
                                <td className="px-3 py-2.5 text-xs text-white/65">{c.endpoint_count ?? 0}</td>
                                <td className="px-3 py-2.5 text-xs"
                                    data-testid={`connection-flowcount-${c.id}`}>
                                    {c.flow_count ? (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono border border-violet-300/30 bg-violet-300/10 text-violet-200"
                                            title={`Used by ${c.flow_count} flow version${c.flow_count === 1 ? "" : "s"}`}>
                                            {c.flow_count}
                                        </span>
                                    ) : (
                                        <span className="text-white/30 font-mono">0</span>
                                    )}
                                </td>
                                <td className="px-3 py-2.5 text-[11px]">
                                    {c.last_test_at == null ? (
                                        <span className="text-white/30 font-mono">—</span>
                                    ) : c.last_test_success ? (
                                        <span className="inline-flex items-center gap-1 text-emerald-200">
                                            <CheckCircle2 className="w-3 h-3" /> ok
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center gap-1 text-rose-200">
                                            <AlertTriangle className="w-3 h-3" /> fail
                                        </span>
                                    )}
                                </td>
                                <td className="px-3 py-2.5 text-[10px] text-white/45">
                                    {c.updated_at?.slice(0, 16).replace("T", " ")}
                                </td>
                                <td className="px-3 py-2.5">
                                    <Link to={`/connections/${c.id}`}
                                        className="inline-flex items-center gap-1 text-[11px] text-white/60 hover:text-white">
                                        Open <ArrowUpRight className="w-3 h-3" />
                                    </Link>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Foot — security blurb */}
            <div className="text-[10px] font-mono text-white/40 inline-flex items-center gap-2">
                <Shield className="w-3 h-3" /> Secrets are AES-256-GCM encrypted at rest and never returned after save.
                <Globe className="w-3 h-3 ml-2" /> Outbound URLs validated against the SSRF deny-list and per-tenant host allowlist.
            </div>

            {createOpen && (
                <CreateModal
                    onClose={() => setCreateOpen(false)}
                    onCreated={(c) => {
                        setCreateOpen(false);
                        load();
                        toast.success(`Created ${c.name} — open it to add endpoints`);
                    }}
                    defaultClientId={filter.client_id}
                />
            )}
        </div>
    );
}
